/**
 * Media Generation Store
 *
 * Tracks per-element media generation status (pending → generating → done/failed).
 * Drives skeleton loading in slide renderer components.
 * Persistence is handled by IndexedDB (mediaFiles table), not Zustand middleware.
 */

import { create } from 'zustand';
import type { MediaGenerationRequest } from '@/lib/media/types';
import { db } from '@/lib/utils/database';
import { createLogger } from '@/lib/logger';

const log = createLogger('MediaGenerationStore');

// ==================== Types ====================

export type MediaTaskStatus = 'pending' | 'generating' | 'done' | 'failed';

export interface MediaTask {
  elementId: string;
  type: 'image' | 'video';
  status: MediaTaskStatus;
  prompt: string;
  params: {
    aspectRatio?: string;
    style?: string;
    duration?: number;
  };
  objectUrl?: string; // URL.createObjectURL() for rendering
  poster?: string; // Video poster objectUrl
  error?: string;
  errorCode?: string; // Structured error code (e.g. 'CONTENT_SENSITIVE')
  retryCount: number;
  stageId: string;
}

interface MediaGenerationState {
  tasks: Record<string, MediaTask>;

  // Batch enqueue
  enqueueTasks: (stageId: string, requests: MediaGenerationRequest[]) => void;

  // Status transitions
  markGenerating: (elementId: string) => void;
  markDone: (elementId: string, objectUrl: string, poster?: string) => void;
  markFailed: (elementId: string, error: string, errorCode?: string) => void;

  // Retry support
  markPendingForRetry: (elementId: string) => void;

  // Queries
  getTask: (elementId: string) => MediaTask | undefined;
  isReady: (elementId: string) => boolean;

  // Restore from IndexedDB on page load
  restoreFromDB: (stageId: string) => Promise<void>;

  // Cleanup
  clearStage: (stageId: string) => void;
  revokeObjectUrls: () => void;
}

// ==================== Helper ====================

/** Check if a src string is a generated media placeholder ID */
export function isMediaPlaceholder(src: string): boolean {
  return /^gen_(img|vid)_[\w-]+$/i.test(src);
}

// ==================== Store ====================

export const useMediaGenerationStore = create<MediaGenerationState>()((set, get) => ({
  tasks: {},

  enqueueTasks: (stageId, requests) => {
    const newTasks: Record<string, MediaTask> = {};
    for (const req of requests) {
      // Skip if already tracked
      if (get().tasks[req.elementId]) continue;
      newTasks[req.elementId] = {
        elementId: req.elementId,
        type: req.type,
        status: 'pending',
        prompt: req.prompt,
        params: {
          aspectRatio: req.aspectRatio,
          style: req.style,
        },
        retryCount: 0,
        stageId,
      };
    }
    if (Object.keys(newTasks).length > 0) {
      set((s) => ({ tasks: { ...s.tasks, ...newTasks } }));
    }
  },

  markGenerating: (elementId) =>
    set((s) => {
      const task = s.tasks[elementId];
      if (!task) return s;
      return {
        tasks: { ...s.tasks, [elementId]: { ...task, status: 'generating' } },
      };
    }),

  markDone: (elementId, objectUrl, poster) =>
    set((s) => {
      const task = s.tasks[elementId];
      if (!task) return s;
      return {
        tasks: {
          ...s.tasks,
          [elementId]: {
            ...task,
            status: 'done',
            objectUrl,
            poster,
            error: undefined,
          },
        },
      };
    }),

  markFailed: (elementId, error, errorCode) =>
    set((s) => {
      const task = s.tasks[elementId];
      if (!task) return s;
      return {
        tasks: {
          ...s.tasks,
          [elementId]: { ...task, status: 'failed', error, errorCode },
        },
      };
    }),

  markPendingForRetry: (elementId) =>
    set((s) => {
      const task = s.tasks[elementId];
      if (!task) return s;
      return {
        tasks: {
          ...s.tasks,
          [elementId]: {
            ...task,
            status: 'pending',
            error: undefined,
            errorCode: undefined,
            retryCount: task.retryCount + 1,
          },
        },
      };
    }),

  getTask: (elementId) => get().tasks[elementId],

  isReady: (elementId) => get().tasks[elementId]?.status === 'done',

  restoreFromDB: async (stageId) => {
    try {
      const records = await db.mediaFiles.where('stageId').equals(stageId).toArray();
      const restored: Record<string, MediaTask> = {};
      for (const rec of records) {
        // Extract elementId from compound key (stageId:elementId)
        const elementId = rec.id.includes(':') ? rec.id.split(':').slice(1).join(':') : rec.id;
        const params = JSON.parse(rec.params || '{}');

        if (rec.error) {
          // Restore as failed task (persisted non-retryable error)
          restored[elementId] = {
            elementId,
            type: rec.type,
            status: 'failed',
            prompt: rec.prompt,
            params,
            error: rec.error,
            errorCode: rec.errorCode,
            retryCount: 0,
            stageId,
          };
        } else if (!rec.blob || rec.blob.size === 0) {
          // 【2026-07-02 P0 FIX v12】空 blob → 标记为 failed 而非 done
          // 真因: 早期版本 (v9 之前) image generation 在某些 provider (MiniMax/Qwen-Image)
          // 偶发把空 blob 写入 IndexedDB — 可能因为 /api/proxy-media CORS 漏掉 content-length
          // 或者 fetchAsBlob race。后果: 刷新后 image 元素 load 空 src → 看起来"PPT空白"
          // (实际 slide 文字+shape 都渲染好了,只有 image 元素空白)。
          // 修法: restore 时 blob.size === 0 → 当 failed 处理 → BaseImageElement
          // 显示 Retry 按钮 + errorCode=BLOB_EMPTY → 用户主动 retry → 正常保存
          // (新生成会走完整的 generateSingleMedia → fetchAsBlob → put 流程,blob 非空)
          log.warn(`Restoring empty blob for ${elementId} as failed (BLOB_EMPTY)`);
          restored[elementId] = {
            elementId,
            type: rec.type,
            status: 'failed',
            prompt: rec.prompt,
            params,
            error: 'Generated media blob is empty (was not saved correctly on first generation). Click Retry to regenerate.',
            errorCode: 'BLOB_EMPTY',
            retryCount: 0,
            stageId,
          };
        } else {
          // Re-wrap blob with stored mimeType — IndexedDB may drop Blob.type
          const blob = rec.blob.type ? rec.blob : new Blob([rec.blob], { type: rec.mimeType });
          const objectUrl = URL.createObjectURL(blob);
          const poster = rec.poster ? URL.createObjectURL(rec.poster) : undefined;
          restored[elementId] = {
            elementId,
            type: rec.type,
            status: 'done',
            prompt: rec.prompt,
            params,
            objectUrl,
            poster,
            retryCount: 0,
            stageId,
          };
        }
      }
      if (Object.keys(restored).length > 0) {
        set((s) => {
          // 【2026-06-04 修 blob ERR_FILE_NOT_FOUND】Strict Mode 双 mount 竞态防御：
          // 撤销同 stageId 的旧 task 的 blob（即使它已无效，revoke 也是 no-op，安全）。
          // 这保证最终 store 里同 stageId 的 task 只有最新一次 restoreFromDB 写入的版本。
          for (const [, t] of Object.entries(s.tasks)) {
            if (t.stageId === stageId && t.objectUrl) {
              URL.revokeObjectURL(t.objectUrl);
              if (t.poster) URL.revokeObjectURL(t.poster);
            }
          }
          // 只保留其他 stage 的 task + 当前 stage 的 restored
          const remaining: Record<string, MediaTask> = {};
          for (const [id, t] of Object.entries(s.tasks)) {
            if (t.stageId !== stageId) remaining[id] = t;
          }
          return { tasks: { ...remaining, ...restored } };
        });
      }
    } catch (err) {
      log.error('Failed to restore from DB:', err);
    }
  },

  clearStage: (stageId) =>
    set((s) => {
      const remaining: Record<string, MediaTask> = {};
      for (const [id, task] of Object.entries(s.tasks)) {
        if (task.stageId !== stageId) {
          remaining[id] = task;
        } else if (task.objectUrl) {
          URL.revokeObjectURL(task.objectUrl);
          if (task.poster) URL.revokeObjectURL(task.poster);
        }
      }
      return { tasks: remaining };
    }),

  revokeObjectUrls: () => {
    // 【2026-06-04 修 blob ERR_FILE_NOT_FOUND】
    // 原版只撤销 blob URL，不调 set，导致：
    // 1. store 里 task.objectUrl 仍是已撤销的 URL 字符串
    // 2. subscribers 收不到通知，React 不重渲染
    // 3. 后续 selector 仍返回该 task，组件用失效 URL 渲染 <img src> → fetch → ERR_FILE_NOT_FOUND
    // 修复：同步撤销 + 清空字段 + setState 通知 React 重新渲染
    const tasks = get().tasks;
    for (const task of Object.values(tasks)) {
      if (task.objectUrl) URL.revokeObjectURL(task.objectUrl);
      if (task.poster) URL.revokeObjectURL(task.poster);
    }
    set((s) => {
      const cleared: Record<string, MediaTask> = {};
      for (const [id, t] of Object.entries(s.tasks)) {
        cleared[id] = { ...t, objectUrl: undefined, poster: undefined };
      }
      return { tasks: cleared };
    });
  },
}));
